<template>
  <div class="container mt-5">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-university me-2 text-warning"></i>
        {{ $t("bankDetails.title") || "Add Bank Details" }}
      </h4>

      <!-- Loader -->
      <div v-if="!ready" class="text-center py-4 text-muted">
        {{ $t("bankDetails.loading") || "Chargement..." }}
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="handleSubmit">
        <div class="row">
          <!-- Bank -->
          <div class="col-md-6 mb-4">
            <div class="p-3 shadow-sm rounded border">
              <label class="form-label fw-bold" style="color:#b03a45;">
                {{ $t("bankDetails.fields.bank") || "Bank" }}
              </label>
              <select v-model="form.bank_id" class="form-select">
                <option disabled value="">
                  {{ $t("bankDetails.placeholders.select") || "Select a bank" }}
                </option>
                <option v-for="bank in banks" :key="bank.id" :value="bank.id">
                  {{ bank.name }}
                </option>
              </select>
              <small v-if="validationErrors.bank_id" class="text-danger">
                {{ validationErrors.bank_id[0] }}
              </small>
            </div>
          </div>

          <!-- Payment Method -->
          <div class="col-md-6 mb-4">
            <div class="p-3 shadow-sm rounded border">
              <label class="form-label fw-bold" style="color:#b03a45;">
                {{ $t("bankDetails.fields.paymentMethod") || "Payment Method" }}
              </label>
              <select v-model="form.payment_method" class="form-select">
                <option value="Bank">Bank</option>
                <option value="Cash">Cash</option>
              </select>
              <small v-if="validationErrors.payment_method" class="text-danger">
                {{ validationErrors.payment_method[0] }}
              </small>
            </div>
          </div>

          <!-- RIB -->
          <div class="col-md-12 mb-4">
            <div class="p-3 shadow-sm rounded border">
              <label class="form-label fw-bold" style="color:#b03a45;">
                {{ $t("bankDetails.fields.rib") || "RIB" }}
              </label>
              <input
                v-model="form.rib"
                type="text"
                class="form-control"
                :placeholder="$t('bankDetails.placeholders.rib') || 'Enter RIB'"
              />
              <small v-if="validationErrors.rib" class="text-danger">
                {{ validationErrors.rib[0] }}
              </small>
            </div>
          </div>
        </div>

        <!-- Buttons -->
        <div class="d-flex justify-content-between mt-4">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="handleBack"
          >
            {{ $t("bankDetails.buttons.back") || "Back" }}
          </button>

          <button
            type="submit"
            class="btn text-white px-4"
            style="background-color:#b03a45;"
          >
            {{ $t("bankDetails.buttons.save") || "Save" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/api";
import { useToast } from "vue-toastification";

const route = useRoute();
const router = useRouter();
const toast = useToast();

// user_id passé en query : ?user_id=12
const userId = String(route.query.user_id || "");

const ready = ref(false);
const validationErrors = ref({});
const banks = ref([]);

const form = reactive({
  user_id: userId,
  bank_id: "",
  payment_method: "Bank",
  rib: "",
});

// Charger les banques
const fetchBanks = async () => {
  try {
    const res = await axios.get("/banks");
    const raw = res.data;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : [];
    banks.value = list;
  } catch {
    banks.value = [];
    toast.error("Erreur lors du chargement des banques");
  }
};

// Soumission du formulaire
const handleSubmit = async () => {
  validationErrors.value = {};

  if (!userId) {
    toast.error("L'identifiant utilisateur est manquant dans l'URL");
    return;
  }

  const payload = {
    user_id: form.user_id,
    bank_id: form.bank_id || null,
    payment_method: form.payment_method,
    rib: form.rib || null,
  };

  try {
    await axios.post("/employee/bank-details", payload);
    toast.success("Détails bancaires enregistrés avec succès");
    handleNext();
  } catch (err) {
    if (err.response?.status === 422) {
      validationErrors.value = err.response.data.errors || {};
      const msg = Object.values(validationErrors.value).flat().join(", ");
      toast.error(msg || "Erreur de validation");
    } else if (err.response?.status === 403) {
      toast.error("Accès refusé : vérifie ton authentification");
    } else {
      toast.error("Erreur lors de l'enregistrement des informations bancaires");
    }
  }
};

// Navigation
const handleBack = () => {
  router.push({
    path: "/employee/professional-information/edit",
    query: { user_id: userId },
  });
};

const handleNext = () => {
  router.push({
    path: "/employee/information/documents",
    query: { user_id: userId },
  });
};

// Initialisation
onMounted(async () => {
  if (!userId) {
    toast.error("User ID manquant");
    ready.value = true;
    return;
  }
  await fetchBanks();
  ready.value = true;
});
</script>

<style scoped>
.btn {
  border-radius: 10px;
}
</style>
